<template>
  <div class="seleccion" v-if="sucursal">
    <caja-header eyebrow="Punto de venta · Paso 2 de 3" titulo="Elegí tu caja" :sucursal="sucursal.nombre">
      <span class="avatar">{{ inicial(user && user.username) }}</span>
      <span>
        <b>{{ user && user.username }}</b>
        <v-btn text x-small color="grey" class="ml-2" @click="goToSucursales">
          <v-icon x-small left>mdi-swap-horizontal</v-icon> Cambiar de sucursal
        </v-btn>
      </span>
    </caja-header>

    <v-alert v-if="errorFetch" dense text type="error" border="left">
      No se pudieron obtener las cajas de la sucursal.
    </v-alert>

    <div class="grilla" v-if="loading">
      <div v-for="i in 3" :key="i" class="tarjeta esqueleto"></div>
    </div>

    <div class="grilla" v-else-if="cajas.length">
      <button
        v-for="caja in cajas"
        :key="caja.id"
        type="button"
        class="tarjeta"
        :class="[estado(caja).clase, { deshabilitada: !puedeElegir(caja), eligiendo: eligiendo === caja.id }]"
        :disabled="!puedeElegir(caja) || eligiendo !== null"
        @click="elegir(caja)"
      >
        <div class="tarjeta-top">
          <v-icon size="30" :color="estado(caja).color">mdi-cash-register</v-icon>
          <v-btn
            icon
            small
            class="config"
            title="Configurar la caja"
            @click.stop="configurar(caja)"
          >
            <v-icon small>mdi-cog-outline</v-icon>
          </v-btn>
        </div>
        <div class="nombre">{{ caja.nombre }}</div>
        <div class="descripcion" v-if="caja.descripcion">{{ caja.descripcion }}</div>
        <div class="estado">
          <span class="punto" :style="{ background: estado(caja).color }"></span>
          {{ estado(caja).texto }}
        </div>
        <div class="cta" v-if="puedeElegir(caja)">
          {{ estado(caja).cta }}
          <v-icon small color="#e3b08a">mdi-arrow-right</v-icon>
        </div>
      </button>
    </div>

    <v-alert v-else dense text type="info" border="left">
      Esta sucursal no tiene cajas. Un administrador tiene que crearlas primero.
    </v-alert>
  </div>
</template>

<script>
import CajaHeader from "@/components/caja/CajaHeader.vue";
import { mapState, mapMutations, mapActions } from "vuex";

export default {
  name: "Cajas",
  components: { CajaHeader },
  data() {
    return {
      cajas: [],
      loading: false,
      errorFetch: false,
      eligiendo: null,
    };
  },
  computed: {
    ...mapState("auth", ["user"]),
    ...mapState("sucursales", ["sucursal"]),
    esPrivilegiado() {
      const rol = (this.user && this.user.role) || "";
      return ["admin", "owner"].some((r) => rol.includes(r));
    },
    miCajaAbierta() {
      return this.cajas.find((c) => c.ocupada && c.user === this.user.username) || null;
    },
  },
  methods: {
    ...mapMutations("sucursales", ["SET_SUCURSAL", "DELETE_SUCURSAL_COOKIE", "SET_CAJA", "SET_CAJA_ID_COOKIE"]),
    ...mapActions("sucursales", ["fetchArqueoVigente"]),

    inicial(nombre) {
      return (nombre || "?").charAt(0).toUpperCase();
    },
    esMia(caja) {
      return caja.ocupada && caja.user === this.user.username;
    },
    estado(caja) {
      if (this.esMia(caja)) {
        return {
          clase: "mia",
          color: "#BA6B30",
          texto: `Tu caja · abierta ${this.desde(caja)}`,
          cta: "Seguir en esta caja",
        };
      }
      if (caja.ocupada) {
        return {
          clase: "ocupada",
          color: "#8a8a8a",
          texto: `Ocupada por ${caja.user} ${this.desde(caja)}`,
          cta: "Usar esta caja",
        };
      }
      return { clase: "libre", color: "#66bb6a", texto: "Disponible", cta: "Abrir esta caja" };
    },
    desde(caja) {
      const inicio = caja.arqueo && caja.arqueo.fechaInicio;
      return inicio ? this.moment(inicio).calendar(null, {
        sameDay: "[hoy] HH:mm",
        lastDay: "[ayer] HH:mm",
        lastWeek: "dddd HH:mm",
        sameElse: "DD/MM HH:mm",
      }) : "";
    },
    // Si ya tengo una caja abierta, sólo puedo volver a esa. Una caja de otra
    // persona sólo la puede tomar un dueño/admin.
    puedeElegir(caja) {
      if (this.miCajaAbierta) return this.esMia(caja);
      if (caja.ocupada) return this.esPrivilegiado;
      return true;
    },
    async elegir(caja) {
      this.eligiendo = caja.id;
      this.SET_CAJA(caja);
      this.SET_CAJA_ID_COOKIE(caja.id);
      try {
        // El backend resuelve el arqueo por la caja recién elegida (header X-Caja)
        const arqueo = await this.fetchArqueoVigente();
        const tieneArqueo = arqueo.success && arqueo.response.data.arqueo_de_caja;
        this.$router.push({ name: tieneArqueo ? "pos.dashboard" : "pos.iniciarCaja" });
      } catch (e) {
        this.eligiendo = null;
        this.$updateBus.$emit("showSnackBar", {
          show: true,
          button: false,
          color: "error",
          text: "No se pudo entrar a la caja. Probá de nuevo.",
          icon: "mdi-alert",
          timeout: 4000,
        });
      }
    },
    configurar(caja) {
      this.$router.push({ name: "cajaSettings", params: { id_caja: caja.id } });
    },
    goToSucursales() {
      this.DELETE_SUCURSAL_COOKIE();
      this.SET_SUCURSAL(null);
      this.$router.push({ name: "pos.seleccionarSucursal" });
    },
    async fetchCajas() {
      this.loading = true;
      this.errorFetch = false;
      try {
        const response = await this.axios.get("/sucursales/cajas");
        this.cajas = response.data;
      } catch (error) {
        this.errorFetch = true;
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    this.fetchCajas();
  },
};
</script>

<style scoped>
.seleccion {
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px 20px 40px;
}
.avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(186, 107, 48, 0.18);
  color: #e3b08a;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}
.grilla {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
  margin-top: 6px;
}
.tarjeta {
  position: relative;
  text-align: left;
  background: #1e1e1e;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 18px;
  padding: 18px 18px 16px;
  min-height: 170px;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: transform 0.12s, border-color 0.12s, background 0.12s;
  color: inherit;
  font: inherit;
}
.tarjeta:hover:not(:disabled) {
  transform: translateY(-2px);
  border-color: rgba(186, 107, 48, 0.6);
  background: #232323;
}
.tarjeta:focus-visible {
  outline: 2px solid #BA6B30;
}
.tarjeta.mia {
  border-color: rgba(186, 107, 48, 0.6);
  background: rgba(186, 107, 48, 0.07);
}
.tarjeta.deshabilitada {
  cursor: not-allowed;
  opacity: 0.45;
}
.tarjeta.eligiendo {
  opacity: 0.7;
}
.tarjeta.esqueleto {
  cursor: default;
  background: linear-gradient(90deg, #1e1e1e 25%, #262626 50%, #1e1e1e 75%);
  background-size: 200% 100%;
  animation: brillo 1.2s infinite;
}
@keyframes brillo {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.tarjeta-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}
.config {
  opacity: 0.5;
}
.config:hover {
  opacity: 1;
}
.nombre {
  font-size: 22px;
  font-weight: 700;
  color: #fff;
}
.descripcion {
  font-size: 13px;
  color: #8a8a8a;
}
.estado {
  margin-top: 6px;
  font-size: 13px;
  color: #cfcfcf;
  display: flex;
  align-items: center;
  gap: 8px;
}
.punto {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}
.cta {
  margin-top: auto;
  padding-top: 14px;
  font-size: 14px;
  font-weight: 600;
  color: #e3b08a;
  display: flex;
  align-items: center;
  gap: 6px;
}
</style>
